import { useState } from 'react';
import { Printer, Smartphone, FileText } from 'lucide-react';
import { Button } from './ui/button';

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const sanitizePhone = (phone) => {
  if (!phone) return '';
  let d = String(phone).replace(/\D/g, '');
  if (d.length === 10) d = '91' + d;
  return d;
};

export const Invoice = ({ order, business }) => {
  const [format, setFormat] = useState('thermal');

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const buildReceiptHtml = () => {
    const isThermal = format === 'thermal';
    const rows = order.items.map((it) => `
      <tr>
        <td class="l">${esc(it.name)}<span class="gst">GST ${esc(it.gst_rate)}%</span></td>
        <td class="c">${esc(it.qty)}</td>
        <td class="r">${Number(it.price).toFixed(0)}</td>
        <td class="r">${Number(it.line_total).toFixed(2)}</td>
      </tr>`).join('');
    const payRows = (order.payments || []).map((p) => `
      <div class="row"><span class="cap">${esc(p.mode)}</span><span>${fmt(p.amount)}</span></div>`).join('');
    const balanceRow = order.balance > 0.01
      ? `<div class="row bal"><span>Balance Due</span><span>${fmt(order.balance)}</span></div>` : '';
    const discountRow = order.discount > 0
      ? `<div class="row"><span>Discount</span><span>-${fmt(order.discount)}</span></div>` : '';

    const pageCss = isThermal
      ? `@page { size: 70mm auto; margin: 0; } body { width: 70mm; }`
      : `@page { size: A4; margin: 12mm; }`;
    const baseFont = isThermal ? '12px' : '13px';

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(order.invoice_no)}</title>
    <style>
      ${pageCss}
      * { box-sizing: border-box; }
      body { font-family: ${isThermal ? "'Courier New', monospace" : 'Arial, sans-serif'}; font-size: ${baseFont}; color: #000; margin: 0; padding: ${isThermal ? '6px 8px' : '0'}; }
      .center { text-align: center; }
      .logo { max-height: 48px; margin: 0 auto 6px; display: block; }
      .name { font-weight: 700; font-size: ${isThermal ? '14px' : '18px'}; }
      .sm { font-size: ${isThermal ? '10px' : '11px'}; line-height: 1.3; }
      .sep { border-top: 1px dashed #555; margin: 6px 0; }
      .row { display: flex; justify-content: space-between; }
      table { width: 100%; border-collapse: collapse; }
      th, td { padding: 2px 0; }
      th { border-bottom: 1px solid #333; font-size: 11px; }
      td { border-bottom: 1px dashed #ccc; vertical-align: top; }
      .l { text-align: left; } .c { text-align: center; } .r { text-align: right; }
      td.c, th.c, td.r, th.r { padding-left: 6px; }
      .gst { display: block; font-size: 9px; color: #666; }
      .total { font-weight: 700; font-size: ${isThermal ? '14px' : '15px'}; border-top: 1px solid #333; padding-top: 4px; margin-top: 4px; }
      .cap { text-transform: capitalize; }
      .bal { font-weight: 600; }
      .foot { text-align: center; margin-top: 12px; font-size: 10px; color: #555; }
    </style></head><body>
      <div class="center">
        <div class="name">${esc(business?.legal_name || '')}</div>
        <div class="sm">${esc(business?.address || '')}</div>
        ${business?.gstin ? `<div class="sm">GSTIN: ${esc(business.gstin)}</div>` : ''}
        ${business?.fssai ? `<div class="sm">FSSAI: ${esc(business.fssai)}</div>` : ''}
        ${business?.phone ? `<div class="sm">${esc(business.phone)}</div>` : ''}
      </div>
      <div class="sep"></div>
      <div class="row"><span>Invoice:</span><span><b>${esc(order.invoice_no)}</b></span></div>
      <div class="row"><span>Date:</span><span>${esc(new Date(order.created_at).toLocaleString('en-IN'))}</span></div>
      ${order.customer_name ? `<div class="row"><span>Customer:</span><span>${esc(order.customer_name)}</span></div>` : ''}
      ${order.customer_phone ? `<div class="row"><span>Phone:</span><span>${esc(order.customer_phone)}</span></div>` : ''}
      <div class="sep"></div>
      <table>
        <thead><tr><th class="l">Item</th><th class="c">Qty</th><th class="r">Rate</th><th class="r">Amt</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="sep"></div>
      <div class="row"><span>Taxable Value</span><span>${fmt(order.subtotal)}</span></div>
      <div class="row"><span>CGST</span><span>${fmt(order.tax_total / 2)}</span></div>
      <div class="row"><span>SGST</span><span>${fmt(order.tax_total / 2)}</span></div>
      ${discountRow}
      <div class="row total"><span>TOTAL</span><span>${fmt(order.grand_total)}</span></div>
      <div class="sep"></div>
      ${payRows}
      ${balanceRow}
      <div class="foot">${esc(business?.invoice_footer || '')}</div>
    </body></html>`;
  };

  const handlePrint = () => {
    const html = buildReceiptHtml();
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const triggerPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
    const logo = doc.querySelector('img.logo');
    if (logo && !logo.complete) {
      logo.onload = triggerPrint;
      logo.onerror = triggerPrint;
      setTimeout(triggerPrint, 1500);
    } else {
      setTimeout(triggerPrint, 200);
    }
  };

  const sendWhatsApp = () => {
    const lines = [];
    lines.push(`Hi ${order.customer_name || 'there'}! 🎂`);
    lines.push(`Thank you for shopping at ${business?.legal_name || 'our store'}.`);
    lines.push('');
    lines.push(`*Invoice:* ${order.invoice_no}`);
    order.items.forEach((it) => {
      lines.push(`• ${it.name} x${it.qty} — ${fmt(it.line_total)}`);
    });
    lines.push('');
    lines.push(`*Total: ${fmt(order.grand_total)}*`);
    if (order.amount_paid != null) lines.push(`Paid: ${fmt(order.amount_paid)}`);
    if (order.balance > 0.01) {
      lines.push(`*Balance Due: ${fmt(order.balance)}*`);
      if (business?.upi_id) {
        lines.push('');
        lines.push(`Pay the balance via UPI: ${business.upi_id}`);
      }
    }
    if (business?.invoice_footer) {
      lines.push('');
      lines.push(business.invoice_footer);
    }
    const text = encodeURIComponent(lines.join('\n'));
    const num = sanitizePhone(order.customer_phone);
    const url = num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  if (!order) return null;

  return (
    <div data-testid="invoice-view">
      <div className="flex gap-2 mb-4 no-print flex-wrap">
        <Button
          data-testid="invoice-thermal-btn"
          variant={format === 'thermal' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFormat('thermal')}
        >
          <Smartphone size={16} className="mr-2" /> Thermal 70mm
        </Button>
        <Button
          data-testid="invoice-a4-btn"
          variant={format === 'a4' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFormat('a4')}
        >
          <FileText size={16} className="mr-2" /> A4
        </Button>
        <Button data-testid="invoice-whatsapp-btn" size="sm" className="ml-auto bg-[#25D366] hover:bg-[#1ebe5d] text-white" onClick={sendWhatsApp}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true">
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.715zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
          </svg>
          WhatsApp
        </Button>
        <Button data-testid="invoice-print-btn" size="sm" className="bg-primary text-white" onClick={handlePrint}>
          <Printer size={16} className="mr-2" /> Print / PDF
        </Button>
      </div>

      <div
        id="invoice-print"
        className={`mx-auto bg-white text-stone-900 border border-stone-200 ${
          format === 'thermal' ? 'invoice-thermal w-[300px] p-4 text-xs font-mono' : 'invoice-a4 w-full p-8 text-sm'
        }`}
      >
        <div className="text-center mb-3">
          <div className="font-bold text-base">{business?.legal_name}</div>
          <div className="text-[10px] leading-tight">{business?.address}</div>
          {business?.gstin && <div className="text-[10px]">GSTIN: {business.gstin}</div>}
          {business?.fssai && <div className="text-[10px]">FSSAI: {business.fssai}</div>}
          {business?.phone && <div className="text-[10px]">{business.phone}</div>}
        </div>

        <div className="border-t border-b border-dashed border-stone-400 py-2 my-2 text-[11px]">
          <div className="flex justify-between"><span>Invoice:</span><span className="font-semibold">{order.invoice_no}</span></div>
          <div className="flex justify-between"><span>Date:</span><span>{new Date(order.created_at).toLocaleString('en-IN')}</span></div>
          {order.customer_name && <div className="flex justify-between"><span>Customer:</span><span>{order.customer_name}</span></div>}
          {order.customer_phone && <div className="flex justify-between"><span>Phone:</span><span>{order.customer_phone}</span></div>}
        </div>

        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-stone-300">
              <th className="text-left py-1">Item</th>
              <th className="text-center py-1">Qty</th>
              <th className="text-right py-1">Rate</th>
              <th className="text-right py-1">Amt</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it, i) => (
              <tr key={i} className="border-b border-dashed border-stone-200">
                <td className="py-1">{it.name}<span className="block text-[9px] text-stone-500">GST {it.gst_rate}%</span></td>
                <td className="text-center py-1">{it.qty}</td>
                <td className="text-right py-1">{Number(it.price).toFixed(0)}</td>
                <td className="text-right py-1">{Number(it.line_total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="border-t border-dashed border-stone-400 mt-2 pt-2 text-[11px] space-y-1">
          <div className="flex justify-between"><span>Taxable Value</span><span>{fmt(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>CGST</span><span>{fmt(order.tax_total / 2)}</span></div>
          <div className="flex justify-between"><span>SGST</span><span>{fmt(order.tax_total / 2)}</span></div>
          {order.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>-{fmt(order.discount)}</span></div>}
          <div className="flex justify-between font-bold text-sm border-t border-stone-300 pt-1 mt-1">
            <span>TOTAL</span><span>{fmt(order.grand_total)}</span>
          </div>
        </div>

        <div className="border-t border-dashed border-stone-400 mt-2 pt-2 text-[11px]">
          {order.payments.map((p, i) => (
            <div key={i} className="flex justify-between"><span className="capitalize">{p.mode}</span><span>{fmt(p.amount)}</span></div>
          ))}
          {order.balance > 0.01 && (
            <div className="flex justify-between font-semibold text-functional-error"><span>Balance Due</span><span>{fmt(order.balance)}</span></div>
          )}
        </div>

        <div className="text-center mt-4 text-[10px] text-stone-500">{business?.invoice_footer}</div>
      </div>
    </div>
  );
};
